import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Education, ApiError, PaginatedResponse, EducationQueryParams, Language } from '../../types/api';

interface UseEducationState {
  education: Education[];
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
}

interface UseEducationReturn extends UseEducationState {
  fetchEducation: (params?: { page?: number; limit?: number }) => Promise<void>;
  getEducationById: (id: string) => Promise<Education | null>;
  refresh: () => Promise<void>;
}

export const useEducation = (
  initialParams?: EducationQueryParams,
  autoFetch: boolean = true,
  language: Language = 'pt'
): UseEducationReturn => {
  const [state, setState] = useState<UseEducationState>({
    education: [],
    loading: false,
    error: null,
    pagination: null,
  });

  const [queryParams, setQueryParams] = useState<{ page?: number; limit?: number }>(
    initialParams || { page: 1, limit: 20 }
  );

  const fetchEducation = useCallback(async (params?: EducationQueryParams) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response: PaginatedResponse<Education> = await api.education.getAll({
        ...(params || queryParams),
        language,
      });
      setState({
        education: response.data,
        loading: false,
        error: null,
        pagination: response.pagination,
      });
      if (params) setQueryParams(params);
    } catch (error: any) {
      setState((prev) => ({ ...prev, loading: false, error }));
    }
  }, [language, queryParams]);

  const getEducationById = useCallback(async (id: string): Promise<Education | null> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const education = await api.education.getById(id);
      setState((prev) => ({ ...prev, loading: false }));
      return education;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as ApiError,
      }));
      return null;
    }
  }, []);

  const refresh = useCallback(() => {
    return fetchEducation(queryParams);
  }, [fetchEducation, queryParams]);

  useEffect(() => {
    if (autoFetch) {
      fetchEducation();
    }
  }, [autoFetch, fetchEducation]);

  return {
    ...state,
    fetchEducation,
    getEducationById,
    refresh,
  };
};
